// ./src/components/generic/TaxSelect/TaxSelect.tsx
//
// THE tax-rate picker for the whole app — never build another one.
//
// ⚠⚠ IT EXISTS BECAUSE THERE WERE ABOUT TO BE TWO. The event's selling settings built its options
// inline, and the ticket-type editor — where `ticketType.taxSettingId` is honoured by the server —
// had no picker at all, so adding one meant either copying that block or sharing it. Two copies
// would have drifted on the first change to how a rate is labelled, and the label is the whole
// point: "Standard VAT · 20%" and "Standard VAT · 20% (included)" are different deals.
//
// ⚠ THE LABEL STATES INCLUSIVE-NESS, ALWAYS. A rate that is already inside the price and one that
// is added on top look identical as a name and a number, and picking the wrong one changes what
// every guest is charged. The list says which it is; so does the hint under the field.
//
// #77→#78 (ROADMAP A2e) — the pinned "Create a tax rate" action opens the REAL `TaxSettingEditDrawer` (lazily — it is a
// feature component and this picker is generic) and selects the new rate on save.
// ⚠ THE DRAWER NEEDS ITS PROVIDER ([[reused-drawer-needs-its-provider]]): it throws outside a `TaxSettingsProvider`, and
//   no page that uses this picker mounts one. So the picker brings its own (idle — it only creates), unless one is above.
// ⚠ THE NEW RATE MUST BE IN THE LIST THE PICKER SHOWS, or the selection reads blank: a caller that passes `taxSettings`
//   owns that list, and a provider that only creates never reloads it. The created rows are merged in here.
// ⚠ NOT FOR CREW ON SOMEONE ELSE'S EVENT: a rate belongs to the account that creates it, and an event or ticket only
//   takes a rate from its OWN account — a crew member's new rate would be refused on save.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DEFAULT_PAGE_SIZE } from "@/config";
import { SelectField } from "@/components/generic/form";
import { TaxSettingsProvider, useOptionalTaxSettingsContext } from "@/contexts/TaxSettingsContext";
import { useLazyDrawer } from "@/hooks/useDrawerMount";
import { useToast } from "@/hooks/useToast";
import { useTaxSettings } from "@/hooks/useTaxSettings";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";
import { TaxSetting, WHOLE_SET } from "@/utils/api";

import { withCreatedTaxes } from "./taxRows";

import type { TaxSettingEditDrawerProps } from "@/components/features/TaxSettings/TaxSettingEditDrawer/TaxSettingEditDrawer";

// ⚠ NOT React.lazy. A lazy component suspends on its first render and then appears ALREADY open, and Mantine animates a
//   drawer only when `opened` goes false → true AFTER it mounted — so it popped in with no slide (owner, 2026-10-02).
//   `useLazyDrawer` loads the code on the click, mounts the drawer closed and opens it on the next commit (the pattern
//   was born here and lifted into the hook, 2026-10-03). Type-only import above.
const loadTaxDrawer = () =>
  import("@/components/features/TaxSettings/TaxSettingEditDrawer/TaxSettingEditDrawer").then((m) => m.TaxSettingEditDrawer);

export interface TaxSelectProps {
  name?: string;
  label?: string;
  description?: string;
  /** The selected `taxSetting.id`, or null for "no tax". */
  value?: string | null;
  onChange?: (name: string | undefined, value: string | null) => void;
  /** False keeps the underlying list idle — a non-ticketed event never needs rates. */
  enabled?: boolean;
  disabled?: boolean;
  required?: boolean;
  /**
   * What "no selection" means where this is used.
   * ⚠ On a TICKET TYPE it means "use the event's rate", not "no tax" — and those are opposite
   * outcomes for the buyer, so the caller has to say which.
   */
  emptyLabel?: string;
  /** Rows already loaded by the caller, to avoid a second fetch of the same catalog. */
  taxSettings?: TaxSetting[];
  /**
   * A server refusal on this field.
   *
   * ⚠ WITHOUT IT THE REFUSAL DISAPPEARS. `getFormErrors` routes any name listed in a caller's
   * KNOWN_FORM_FIELDS into `fields` and NEVER into genericErrors, so a caller that lists
   * `taxSettingId` and has nowhere to render it gets a save that fails forever with a valid-looking
   * form and nothing on screen. `TicketDrawer` shipped exactly that (found by `npm run form-audit`).
   */
  error?: string | null;
  /** Offer the pinned "Create a tax rate" action (default true; never offered to crew on someone else's event). */
  allowCreate?: boolean;
  /**
   * Told when the "Create a tax rate" drawer opens and closes.
   * ⚠ A CALLER WITH SHORTCUTS MUST SWITCH THEM OFF while it is open ([[fullscreen-modal-in-drawer-esc]]): every drawer
   *   listens on window, so Esc would close the drawer underneath too, and ⌘S would save it as well as the new rate.
   */
  onCreatingChange?: (open: boolean) => void;
}

/** "Standard VAT · 20% (included)" — the name alone never says enough to choose by. */
export const taxOptionLabel = (tax: TaxSetting, includedWord: string): string => {
  const rate = tax.rate == null ? "" : ` · ${tax.rate}%`;
  const inclusive = tax.isInclusive ? ` (${includedWord})` : "";

  return `${tax.name ?? ""}${rate}${inclusive}`;
};

export const TaxSelect: React.FC<TaxSelectProps> = ({
  name = "taxSettingId",
  label,
  description,
  value,
  onChange,
  enabled = true,
  disabled,
  required,
  emptyLabel,
  taxSettings: provided,
  error,
  allowCreate = true,
  onCreatingChange,
}) => {
  const { t } = useTranslation(["events", "common", "taxes"]);
  const toast = useToast();
  // ⚠ Only fetches when the caller did NOT pass rows — a page that already has the catalog
  // (the event form loads it for other reasons) must not pull it twice.
  const { taxSettings: loaded } = useTaxSettings({ enabled: enabled && !provided, pageSize: WHOLE_SET });
  const outerProvider = useOptionalTaxSettingsContext();
  const createDrawer = useLazyDrawer<React.ComponentType<TaxSettingEditDrawerProps>>(loadTaxDrawer, {
    onLoadError: () => toast.error(t("common:errors.drawerLoadFailed", { defaultValue: "Could not open that form — check your connection and try again." })),
  });
  const Editor = createDrawer.Component;
  const createOpen = createDrawer.isOpen;
  const [created, setCreated] = useState<TaxSetting[]>([]);
  const rows = useMemo(() => withCreatedTaxes(provided ?? loaded, created), [provided, loaded, created]);
  // ⚠ FAIL CLOSED inside an event: offered only once the access read says this person is in the event's OWN account
  //   (a rate is created in the caller's account — a crew member's would be out of the event's reach). The ONE rule
  //   every picker follows (#142 `useCreateInPlace`).
  const inEventsAccount = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);
  const canCreate = allowCreate && inEventsAccount && !disabled;

  // ⚠ Through a ref, so a caller passing an inline callback does not re-run this on every render.
  const creatingChangeRef = useRef(onCreatingChange);
  creatingChangeRef.current = onCreatingChange;
  const createOpenRef = useRef(createOpen);
  createOpenRef.current = createOpen;
  useEffect(() => {
    creatingChangeRef.current?.(createOpen);
  }, [createOpen]);
  // ⚠ Unmounted while open (Back closes the drawer this picker sits in): give the caller its shortcuts back, or every
  //   later drawer on that page would open with no Esc, no X and no ⌘S.
  useEffect(
    () => () => {
      if (createOpenRef.current) {
        creatingChangeRef.current?.(false);
      }
    },
    [],
  );

  const startCreate = createDrawer.open;

  const handleCreated = useCallback(
    (tax: TaxSetting) => {
      setCreated((prev) => [...prev, tax]);

      if (tax.id) {
        onChange?.(name, tax.id);
      }
    },
    [name, onChange],
  );

  const included = t("events:selling.taxIncludedWord", { defaultValue: "included" });

  const data = useMemo(
    () =>
      rows.filter((tax) => tax.id).map((tax) => ({ value: tax.id as string, label: taxOptionLabel(tax, included) })),
    [rows, included],
  );

  const placeholder = data.length
    ? (emptyLabel ?? t("events:selling.noTax", { defaultValue: "No tax" }))
    : t("events:selling.noTaxRecords", { defaultValue: "No tax rates yet" });

  // Kept mounted once loaded, so closing slides out too and Mantine hands focus back to the picker.
  const drawer = Editor ? <Editor opened={createDrawer.opened} onClose={createDrawer.close} onSaved={handleCreated} /> : null;

  return (
    <>
      <SelectField
        name={name}
        label={label ?? t("events:selling.taxRecord", { defaultValue: "Tax rate" })}
        description={description}
        data={data}
        value={value ?? null}
        onChange={onChange}
        disabled={disabled}
        required={required}
        clearable
        placeholder={placeholder}
        error={error}
        createLabel={canCreate ? t("taxes:select.create", { defaultValue: "Create a tax rate" }) : undefined}
        onCreate={canCreate ? startCreate : undefined}
      />

      {/* Loaded only once asked for. An idle provider of its own when the page has none — it only creates. */}
      {outerProvider
        ? drawer
        : drawer && <TaxSettingsProvider options={{ autoLoad: false, pageSize: DEFAULT_PAGE_SIZE }}>{drawer}</TaxSettingsProvider>}
    </>
  );
};
